import "@/App.css";
import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import RoleRoute from "@/components/RoleRoute";
import { initSyncEngine } from "@/lib/syncEngine";
import { registerServiceWorker } from "@/lib/registerSW";

import LoginPage from "@/pages/LoginPage";
import TechShell from "@/pages/tech/TechShell";
import TechRoute from "@/pages/tech/TechRoute";
import TechServiceDetail from "@/pages/tech/TechServiceDetail";
import TechReportFlow from "@/pages/tech/TechReportFlow";
import TechReceipt from "@/pages/tech/TechReceipt";
import TechPhotoReport from "@/pages/tech/TechPhotoReport";
import TechFloorPlan from "@/pages/tech/TechFloorPlan";
import TechTransportService from "@/pages/tech/TechTransportService";
import TechSupplies from "@/pages/tech/TechSupplies";
import TechEquipment from "@/pages/tech/TechEquipment";

import PortalShell from "@/pages/portal/PortalShell";
import PortalDashboard from "@/pages/portal/PortalDashboard";
import PortalReports from "@/pages/portal/PortalReports";
import PortalCalendar from "@/pages/portal/PortalCalendar";
import PortalDocuments from "@/pages/portal/PortalDocuments";
import PortalReportDetail from "@/pages/portal/PortalReportDetail";
import PortalInsights from "@/pages/portal/PortalInsights";
import PortalMaps from "@/pages/portal/PortalMaps";
import PortalTransportes from "@/pages/portal/PortalTransportes";
import PortalSupport from "@/pages/portal/PortalSupport";
import PortalUsers from "@/pages/portal/PortalUsers";

import AdminShell from "@/pages/admin/AdminShell";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import { defaultMenuPermissions, firstAllowedAdminPath } from "@/constants/adminMenu";

function AdminIndex() {
    const { user } = useAuth();
    if (user?.role === "admin") return <AdminDashboard />;
    const perms = Array.isArray(user?.menu_permissions) ? user.menu_permissions : defaultMenuPermissions(user?.role);
    if (perms.includes("dashboard")) return <AdminDashboard />;
    return <Navigate to={firstAllowedAdminPath(user)} replace />;
}

import AdminClients from "@/pages/admin/AdminClients";
import AdminTechnicians from "@/pages/admin/AdminTechnicians";
import AdminSchedules from "@/pages/admin/AdminSchedules";
import AdminReports from "@/pages/admin/AdminReports";
import AdminDocuments from "@/pages/admin/AdminDocuments";
import AdminOdooStatus from "@/pages/admin/AdminOdooStatus";
import AdminStorage from "@/pages/admin/AdminStorage";
import AdminFloorPlans from "@/pages/admin/AdminFloorPlans";
import AdminAlerts from "@/pages/admin/AdminAlerts";
import AdminExecutiveReports from "@/pages/admin/AdminExecutiveReports";
import AdminTransportes from "@/pages/admin/AdminTransportes";
import AdminCatalog from "@/pages/admin/AdminCatalog";
import AdminSupplies from "@/pages/admin/AdminSupplies";
import AdminEquipment from "@/pages/admin/AdminEquipment";
import AdminAttendance from "@/pages/admin/AdminAttendance";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminAudits from "@/pages/admin/AdminAudits";

import AuditShell from "@/pages/audit/AuditShell";
import AuditRoute from "@/pages/audit/AuditRoute";
import AuditServiceDetail from "@/pages/audit/AuditServiceDetail";
import AuditFlow from "@/pages/audit/AuditFlow";

import PublicReportView from "@/pages/public/PublicReportView";
import PublicTransportReport from "@/pages/public/PublicTransportReport";

function RootRedirect() {
    const { user } = useAuth();
    if (!user) return <Navigate to="/login" replace />;
    if (user.role === "technician") return <Navigate to="/tech" replace />;
    if (user.role === "client") return <Navigate to="/portal" replace />;
    if (user.role === "auditor") return <Navigate to="/audit" replace />;
    return <Navigate to="/admin" replace />;
}

function App() {
    useEffect(() => {
        document.title = "TRULYN – Servicio de Campo";
        initSyncEngine();
        registerServiceWorker();
    }, []);
    return (
        <div className="App">
            <AuthProvider>
                <BrowserRouter>
                    <Toaster position="top-right" richColors closeButton />
                    <Routes>
                        <Route path="/" element={<RootRedirect />} />
                        <Route path="/login" element={<LoginPage />} />

                        {/* Public QR report viewer */}
                        <Route path="/r/:reportId" element={<PublicReportView />} />
                        <Route path="/rt/:reportId" element={<PublicTransportReport />} />

                        {/* Technician PWA */}
                        <Route
                            path="/tech"
                            element={
                                <RoleRoute roles={["technician", "admin"]}>
                                    <TechShell />
                                </RoleRoute>
                            }
                        >
                            <Route index element={<TechRoute />} />
                            <Route path="service/:id" element={<TechServiceDetail />} />
                            <Route path="service/:id/report" element={<TechReportFlow />} />
                            <Route path="service/:id/photo-report" element={<TechPhotoReport />} />
                            <Route path="service/:id/map" element={<TechFloorPlan />} />
                            <Route path="service/:id/transport" element={<TechTransportService />} />
                            <Route path="supplies" element={<TechSupplies />} />
                            <Route path="equipment" element={<TechEquipment />} />
                            <Route path="service/:id/receipt/:reportId" element={<TechReceipt />} />
                        </Route>

                        {/* Auditor PWA */}
                        <Route
                            path="/audit"
                            element={
                                <RoleRoute roles={["auditor", "admin"]}>
                                    <AuditShell />
                                </RoleRoute>
                            }
                        >
                            <Route index element={<AuditRoute />} />
                            <Route path="service/:id" element={<AuditServiceDetail />} />
                            <Route path="service/:id/flow" element={<AuditFlow />} />
                            <Route path="service/:id/map" element={<TechFloorPlan />} />
                            <Route path="service/:id/transport" element={<TechTransportService />} />
                        </Route>

                        {/* Client portal */}
                        <Route
                            path="/portal"
                            element={
                                <RoleRoute roles={["client", "admin"]}>
                                    <PortalShell />
                                </RoleRoute>
                            }
                        >
                            <Route index element={<PortalDashboard />} />
                            <Route path="reports" element={<PortalReports />} />
                            <Route path="reports/:id" element={<PortalReportDetail />} />
                            <Route path="calendar" element={<PortalCalendar />} />
                            <Route path="documents" element={<PortalDocuments />} />
                            <Route path="insights" element={<PortalInsights />} />
                            <Route path="mapas" element={<PortalMaps />} />
                            <Route path="transportes" element={<PortalTransportes />} />
                            <Route path="soporte" element={<PortalSupport />} />
                            <Route path="usuarios" element={<PortalUsers />} />
                        </Route>

                        {/* Admin panel */}
                        <Route
                            path="/admin"
                            element={
                                <RoleRoute roles={["admin", "operator", "auxiliar"]}>
                                    <AdminShell />
                                </RoleRoute>
                            }
                        >
                            <Route index element={<AdminIndex />} />
                            <Route path="clients" element={<AdminClients />} />
                            <Route path="technicians" element={<AdminTechnicians />} />
                            <Route path="schedules" element={<AdminSchedules />} />
                            <Route path="reports" element={<AdminReports />} />
                            <Route path="documents" element={<AdminDocuments />} />
                            <Route path="floor-plans" element={<AdminFloorPlans />} />
                            <Route path="alerts" element={<AdminAlerts />} />
                            <Route path="executive-reports" element={<AdminExecutiveReports />} />
                            <Route path="transportes" element={<AdminTransportes />} />
                            <Route path="catalogo" element={<AdminCatalog />} />
                            <Route path="insumos" element={<AdminSupplies />} />
                            <Route path="equipos" element={<AdminEquipment />} />
                            <Route path="asistencia" element={<AdminAttendance />} />
                            <Route path="usuarios" element={<AdminUsers />} />
                            <Route path="auditores" element={<AdminAudits />} />
                            <Route path="odoo" element={<AdminOdooStatus />} />
                            <Route path="almacenamiento" element={<AdminStorage />} />
                        </Route>

                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </BrowserRouter>
            </AuthProvider>
        </div>
    );
}

export default App;
